<template>
  <b-card no-body>
    <b-card-body class="p-0 p-md-3">
      <b-list-group class="bg-light">
        <b-list-group-item class="d-md-flex justify-content-between bg-light border-0">
          <div class="fw-bold my-auto">
            <span class="text-capitalize">{{ buildingDetail.name }}</span>
            <div
              v-if="buildingDetail.parent_id"
              class="my-auto py-1 mt-1"
              style="background-color: white;"
            >
              <div class="border border-2 border-top-0 border-end-0 border-bottom-0 border-tosca px-2">
                Part of {{ buildingDetail.parent.name }}
              </div>
            </div>
          </div>
          <router-link
            :to="`/unit/${unitId}/building/${buildingDetail.id}/${selectedMode}`"
            role="button"
            class="d-flex btn btn-detail shadow-sm my-auto"
            style="max-height: 36px;"
          >
            <div class="my-auto">
              <span class="text-capitalize fw-bold">Status Overview</span>
              <i class="mdi mdi-chevron-right" />
            </div>
          </router-link>
        </b-list-group-item>
      </b-list-group>
      <div class="d-md-flex align-items-start mt-3">
        <b-col
          md="5"
          class="d-flex"
          style="height: 200px"
        >
          <div class="d-flex my-auto ms-4">
            <img
              :src="require(`@/assets/images/building-icon-${selectedMode}.png`)"
              class="img img-fluid my-auto mx-auto"
              style="max-height: 100px"
            >
          </div>
          <div class="px-2 w-50 mx-auto my-auto">
            <b-card class="my-auto">
              <div
                class="text-center m-auto"
                style="font-size: 24px"
              >
                <span v-if="!isLoading">
                  {{ formatNumber(parsedPayloadData) }}
                </span>
                {{ payloadUnit }}
              </div>
              <template #footer>
                <h5 class="fw-bold text-capitalize text-center mt-2">
                  {{ payloadKey }}
                </h5>
              </template>
            </b-card>
          </div>
        </b-col>
        <b-col
          md="7"
          class="mt-4"
        >
          <div
            class="my-auto"
            style="height: 200px"
          >
            <Skeleton
              v-if="isLoading"
              height="100%"
            />
            <BarChart
              v-else
              :peak-limit="peakLimit"
              :chart-data="chartData"
              :chart-interval="chartInterval"
              :interval="paramInterval"
              :x-axis-label="chartXAxisLabel"
              :y-axis-label="chartYAxisLabel"
              :enable-grouping="true"
            />
          </div>
        </b-col>
      </div>
    </b-card-body>
  </b-card>
</template>

<script>
import {
  startOfMonth, startOfYear, eachMinuteOfInterval, eachDayOfInterval, eachMonthOfInterval, endOfMonth, endOfYear, format, addYears,
} from 'date-fns';
import { convertToKwh, formatNumber } from '@/utils/helper';
import Echo from '@/services/web-socket';
import BarChart from '@/components/BarChart';
import Skeleton from '@/components/Skeleton';

export default {
  components: {
    BarChart, Skeleton,
  },
  props: {
    buildingDetail: {
      type: Object,
      default() {
        return {};
      },
    },
    selectedMode: {
      type: String,
      default: 'demand',
    },
    payloadKey: {
      type: String,
      default: '',
    },
    timeWindow: {
      type: String,
      default: '',
    },
    startDate: {
      type: Date,
      default() {
        return new Date();
      },
    },
    endDate: {
      type: Date,
      default() {
        return new Date();
      },
    },
  },
  data() {
    return {
      payloads: [],
      payloadsLastYear: [],
      isLoading: false,
    };
  },
  computed: {
    unitId() {
      return this.$route.params.unitId;
    },
    peakLimit() {
      if (this.selectedMode == 'demand' && this.payloadKey != 'energy') {
        return Number(this.buildingDetail?.custom_attributes?.maximum_peak_power) ?? 0;
      }
      return 0;
    },
    paramInterval() {
      if (this.timeWindow == 'day') {
        if (this.payloadKey == 'energy') {
          return 'hourly';
        }
        return 'quarter_hourly';
      } if (this.timeWindow == 'month') {
        return 'daily';
      } if (this.timeWindow == 'year') {
        return 'monthly';
      } return '';
    },
    paramPayloadKeys() {
      // Demand
      if (this.selectedMode == 'demand') {
        if (this.payloadKey == 'energy') {
          return ['Active_Energy_Demand_Consumed'];
        }
        return ['Active_Power_Demand'];
      }
      // Supply
      if (this.selectedMode == 'supply') {
        if (this.payloadKey == 'energy') {
          return ['Active_Energy_Supply_Consumed'];
        }
        return ['Active_Power_Supply'];
      }
      // Total
      if (this.payloadKey == 'energy') {
        return ['Active_Energy_Demand_Consumed', 'Active_Energy_Supply_Consumed'];
      }
      return ['Active_Power_Demand', 'Active_Power_Supply'];
    },
    payloadUnit() {
      if (this.payloadKey == 'energy') {
        return 'kWh';
      }
      return 'kW';
    },
    parsedPayloadData() {
      if (!this.payloads?.length) {
        return 0;
      }
      const flattenData = this.payloads.map((data) => Number(data.cumulative_value));
      // Sum if energy
      if (this.payloadKey == 'energy') {
        return convertToKwh(flattenData.reduce((a, b) => a + b, 0));
      }
      // Select max if not energy
      return convertToKwh(Math.max(...flattenData));
    },
    formatIntervalString() {
      switch (this.paramInterval) {
        case 'quarter_hourly':
          return 'HH:mm';
        case 'hourly':
          return 'HH';
        case 'daily':
          return 'dd';
        case 'monthly':
          return 'MMM';
        default:
          return '';
      }
    },
    chartYAxisLabel() {
      if (this.payloadKey == 'energy') {
        return 'Energy (kWh)';
      }
      return 'Power (kW)';
    },
    chartXAxisLabel() {
      if (this.timeWindow == 'day') {
        return 'time (hrs.)';
      }
      if (this.timeWindow == 'month') {
        return 'time (days.)';
      }
      return 'time (months.)';
    },
    chartInterval() {
      if (this.paramInterval == 'quarter_hourly') {
        const minutesArray = eachMinuteOfInterval({
          start: new Date().setHours(0, 0, 0, 0),
          end: new Date().setHours(23, 59, 0, 0),
        }, { step: 15 });
        return minutesArray.map((item) => format(item, this.formatIntervalString));
      }
      if (this.paramInterval == 'hourly') {
        const minutesArray = eachMinuteOfInterval({
          start: new Date().setHours(0, 0, 0, 0),
          end: new Date().setHours(23, 59, 0, 0),
        }, { step: 60 });
        return minutesArray.map((item) => format(item, this.formatIntervalString));
      }
      if (this.paramInterval == 'daily') {
        const daysArray = eachDayOfInterval({
          start: startOfMonth(new Date()),
          end: endOfMonth(new Date()),
        }, { step: 1 });
        return daysArray.map((item) => format(item, this.formatIntervalString));
      }
      const monthsArray = eachMonthOfInterval({
        start: startOfYear(new Date()),
        end: endOfYear(new Date()),
      }, { step: 1 });
      return monthsArray.map((item) => format(item, this.formatIntervalString));
    },
    chartSeriesData() {
      const valueArray = [];

      this.chartInterval.forEach((item) => (
        valueArray.push(this.payloads.find((payload) => format(new Date(payload.starts_at), this.formatIntervalString) == item))
      ));
      const payloadValue = valueArray.map((item) => (item?.cumulative_value ? Number(convertToKwh(item?.cumulative_value).toFixed(2)) : 0));

      const finalData = [];

      payloadValue.forEach((item) => (
        finalData.push({
          y: item,
          color: this.getChartColor(item),
        })
      ));

      return finalData;
    },
    chartLastYearData() {
      const valueArray = [];
      this.chartInterval.forEach((item) => (
        valueArray.push(this.payloadsLastYear.find((payload) => format(new Date(payload.starts_at), this.formatIntervalString) == item))
      ));
      const finalData = valueArray.map((item) => (item?.cumulative_value ? Number(convertToKwh(item?.cumulative_value).toFixed(2)) : 0));

      return finalData;
    },
    chartData() {
      if (this.timeWindow == 'year') {
        return [
          {
            color: '#D5D4D4',
            data: this.chartLastYearData,
            name: format((addYears(this.startDate, -1)), 'yyyy'),
            pointPadding: 0,
            pointPlacement: 0,
            groupPadding: 0,
            borderColor: 'white',
          },
          {
            data: this.chartSeriesData,
            name: format(this.startDate, 'yyyy'),
            pointPadding: 0,
            pointPlacement: 0,
            groupPadding: 0,
            borderColor: 'white',
          },
        ];
      }
      return [
        {
          color: '#61C219',
          data: this.chartSeriesData,
          name: this.chartYAxisLabel,
          pointPadding: 0,
          pointPlacement: 0,
          groupPadding: 0,
          borderColor: 'white',
        },
      ];
    },
  },
  watch: {
    selectedMode: {
      immediate: false,
      handler() {
        this.getBuildingData();
      },
    },
    timeWindow: {
      immediate: false,
      handler() {
        this.getBuildingData();
      },
    },
    startDate: {
      immediate: false,
      handler() {
        this.getBuildingData();
      },
    },
    payloadKey: {
      immediate: false,
      handler() {
        this.getBuildingData();
      },
    },
    parsedPayloadData: {
      immediate: false,
      handler() {
        this.$emit('updateValue', { id: this.buildingDetail.id, value: this.parsedPayloadData });
      },
    },
  },
  beforeDestroy() {
    // leave channel when leaving page
    this.unsubscribePayload();
  },
  mounted() {
    this.subscribePayload();
    this.getBuildingData();
  },
  methods: {
    formatNumber,
    async getBuildingData() {
      try {
        this.isLoading = true;
        const { data } = await this.$api.getUnitEnergy(this.unitId,
          {
            per_page: 200,
            interval: this.paramInterval,
            start_date: this.startDate,
            end_date: this.endDate,
            block_id: this.buildingDetail.id,
            timezone: 'Asia/Jakarta',
            payload_keys: this.paramPayloadKeys,
            aggregate: 'sum',
          });
        this.payloads = data.data;
        if (this.timeWindow == 'year') {
          this.getLastYearData();
        }
      } catch (error) {
        console.log(error);
      } finally {
        this.isLoading = false;
      }
    },
    async getLastYearData() {
      try {
        const startLastYear = startOfYear(addYears(this.startDate, -1));
        const endLastYear = endOfYear(startLastYear);
        const { data } = await this.$api.getUnitEnergy(this.unitId,
          {
            per_page: 200,
            interval: this.paramInterval,
            start_date: startLastYear,
            end_date: endLastYear,
            block_id: this.buildingDetail.id,
            timezone: 'Asia/Jakarta',
            payload_keys: this.paramPayloadKeys,
            aggregate: 'sum',
          });
        this.payloadsLastYear = data.data;
      } catch (error) {
        console.log(error);
      }
    },
    getChartColor(value) {
      if (this.peakLimit && this.payloadKey != 'energy') {
        if (value >= 0.95 * this.peakLimit) { return '#BC250C'; }
        if (value >= 0.90 * this.peakLimit) { return '#EDB908'; }
      } return '#61C219';
    },
    unsubscribePayload() {
      Echo.leaveChannel(`block.${this.buildingDetail.id}.energy`);
    },
    subscribePayload() {
      Echo.subscribeChannel(`block.${this.buildingDetail.id}.energy`)
      // listen new payloads
        .listen('EnergyCalculated', (data) => {
          if (data.interval == this.paramInterval) {
            if (this.paramPayloadKeys.includes(data.payload_key)) {
              this.getBuildingData();
            }
          }
        });
    },
  },
};
</script>

<style scoped>
.card-footer {
  border-top: 2px solid #009688;
}
</style>
